import React, { type ReactNode } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import ModalCore, { CloseButton, type ModalDialogProps } from '@atlaskit/modal-dialog';
import { type BackgroundColor, Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ModalBody } from './ModalBody';
import { ModalFooter } from './ModalFooter';
import { ModalTitle } from './ModalTitle';

export interface ModalProps extends ModalDialogProps {
	/**
	 * Wrap the actions in a Fragment so that spacing, wrapping, etc are handled by the modal.
	 * @private
	 * @deprecated use primaryAction and secondaryAction instead.
	 */
	actions?: ReactNode;
	/** Optional background color for the modal */
	backgroundColor?: BackgroundColor;
	children: ReactNode;
	includeCloseButton?: boolean;
	logo?: ReactNode;
	/**
	 * The main CTA for the modal. Rendered to the right of the secondary action.
	 */
	primaryAction?: ReactNode;
	/**
	 * When set to true, we will not render the `Modal` from atlaskit. Consumers will need to provide
	 * the `width` and `autoFocus` props themselves.
	 *
	 * When using entrypoints (which is almost all use cases for us) the `Modal` is added automatically by the entrypoint.
	 * This is a platform level optimization for entrypoints (to prevent needing to load the modal code). We also do not
	 * want to load a modal inside of a modal as that could cause UI defects.
	 */
	renderedInsideEntrypoint?: boolean;
	/**
	 * @private
	 * @deprecated use sideContent and sideLocation instead.
	 */
	right?: ReactNode;
	/**
	 * The alternate action for the modal. Rendered to the left of the primary action.
	 */
	secondaryAction?: ReactNode;
	sideContent?: ReactNode;
	/**
	 * Defaults to the right side
	 **/
	sideLocation?: 'left' | 'right';
	subtitle?: ReactNode;
	/**
	 * Optional color for the subtitle text. Defaults to 'color.text.subtle'.
	 */
	subtitleColor?: 'color.text' | 'color.text.subtle';
	/**
	 * The normal expectation is that callers provide a string title. The modal will handle default
	 * styling as a heading. But this is typed as a `ReactNode` to allow callers to customise the
	 * title. The modal will still apply its own styling to it, so depending on the particular set of
	 * customisations, your mileage may vary.
	 * See ../../examples/trial-awareness-with-custom-title-example.tsx for an example customisation.
	 */
	title: ReactNode;
}

const styles = cssMap({
	hasSideContentContainer: {
		position: 'relative',
		overflowY: 'auto',
		height: '100%',
		borderRadius: token('radius.xlarge', '3px'),
		'@media (min-width: 64rem)': {
			display: 'grid',
			gridTemplateColumns: '1fr 1fr',
		},
	},
	baseContainer: {
		position: 'relative',
	},
	stack: {
		height: '100%',
		paddingTop: token('space.600'),
		paddingRight: token('space.600'),
		paddingBottom: token('space.600'),
		paddingLeft: token('space.600'),
		overflowY: 'auto',
	},
	sideContainer: {
		display: 'none',
		'@media (min-width: 64rem)': {
			backgroundColor: token('color.background.accent.gray.subtlest'),
			display: 'flex',
			width: '100%',
			overflowY: 'auto',
		},
	},
	leftSideContainer: {
		'@media (min-width: 64rem)': {
			borderTopLeftRadius: token('radius.xlarge', '3px'),
			borderBottomLeftRadius: token('radius.xlarge', '3px'),
		},
	},
	rightSideContainer: {
		'@media (min-width: 64rem)': {
			borderTopRightRadius: token('radius.xlarge', '3px'),
			borderBottomRightRadius: token('radius.xlarge', '3px'),
		},
	},
	closeButton: {
		position: 'absolute',
		top: token('space.200'),
		right: token('space.200'),
		zIndex: 1,
	},
});

export const Modal = ({
	renderedInsideEntrypoint,
	backgroundColor,
	...modalProps
}: ModalProps): React.JSX.Element => {
	const hasSideContent = !!modalProps.right || !!modalProps.sideContent;

	if (renderedInsideEntrypoint) {
		return (
			<Box
				xcss={cx(styles.baseContainer, hasSideContent && styles.hasSideContentContainer)}
				backgroundColor={backgroundColor}
				style={{ height: modalProps.height }}
			>
				<ModalContent {...modalProps} />
			</Box>
		);
	}

	return (
		<ModalCore width={hasSideContent ? 'x-large' : undefined} {...modalProps}>
			<Box
				xcss={cx(styles.baseContainer, hasSideContent && styles.hasSideContentContainer)}
				backgroundColor={backgroundColor}
			>
				<ModalContent {...modalProps} />
			</Box>
		</ModalCore>
	);
};

const ModalContent = ({
	logo,
	title,
	subtitle,
	subtitleColor = 'color.text.subtle',
	children,
	actions,
	primaryAction,
	secondaryAction,
	right,
	sideContent,
	sideLocation = 'right',
	includeCloseButton = false,
	onClose,
}: ModalProps): React.JSX.Element => {
	const rightContent = right ?? (sideLocation === 'right' ? sideContent : undefined);
	const leftContent = !right && sideLocation === 'left' ? sideContent : undefined;

	const safeOnClose = typeof onClose === 'function' ? onClose : () => {};

	return (
		<>
			{includeCloseButton && (
				<Box xcss={styles.closeButton}>
					<CloseButton testId="modal-close-button" onClick={safeOnClose} />
				</Box>
			)}
			{!!leftContent && (
				<Box xcss={cx(styles.sideContainer, styles.leftSideContainer)}>{leftContent}</Box>
			)}
			<Stack space="space.400" xcss={styles.stack}>
				{logo}
				<Stack space="space.200">
					<ModalTitle>{title}</ModalTitle>
					{subtitle ? (
						<Text color={subtitleColor} size="medium">
							{subtitle}
						</Text>
					) : null}
				</Stack>
				<ModalBody>{children}</ModalBody>
				{actions ? (
					<ModalFooter>{actions}</ModalFooter>
				) : (
					<ModalFooter>
						{secondaryAction}
						{primaryAction}
					</ModalFooter>
				)}
			</Stack>
			{!!rightContent && (
				<Box xcss={cx(styles.sideContainer, styles.rightSideContainer)}>{rightContent}</Box>
			)}
		</>
	);
};
